import { useMemo } from 'react';
import { format, subDays } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Detection } from '@packages/shared-types';

import { LoadingLogo } from '@/components/shared/LoadingLogo';

interface DetectionTrendChartProps {
  detections: Detection[];
  isLoading?: boolean;
}

export function DetectionTrendChart({
  detections,
  isLoading,
}: DetectionTrendChartProps): JSX.Element {
  const chartData = useMemo(() => {
    const sevenDaysAgo = subDays(new Date(), 7);
    const recentDetections = detections.filter(
      (d) => new Date(d.detectedAt) > sevenDaysAgo,
    );

    // Group by date
    const groupedByDate = recentDetections.reduce(
      (acc, detection) => {
        const date = format(new Date(detection.detectedAt), 'yyyy-MM-dd');
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    // Create array for last 7 days
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), 6 - i);
      const dateKey = format(date, 'yyyy-MM-dd');
      return {
        date: format(date, 'MMM d'),
        fullDate: dateKey,
        count: groupedByDate[dateKey] || 0,
      };
    });

    return days;
  }, [detections]);

  if (isLoading) {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-3">
        <LoadingLogo size="sm" />
        <span className="text-sm text-muted-foreground">Loading chart...</span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="date"
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <YAxis
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.5rem',
            color: 'hsl(var(--card-foreground))',
          }}
        />
        <Line
          type="monotone"
          dataKey="count"
          stroke="hsl(var(--chart-primary))"
          strokeWidth={2}
          dot={{ fill: 'hsl(var(--chart-primary))', r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
